/**
 * Read adapters for stored podium payloads (plots plan E; contract in
 * `../contract.ts`, shapes in docs/deck-design-system/plots/read-adapters.md).
 *
 * Shape only: they rename and regroup what Python already stamped. The two
 * legacy readers keep today's rules for stored payloads that predate a stamp:
 * a deck slide stored before `plot` (today's chrome strings and the adaptive
 * display rounding the slide applied in TS), and a summary card stored before
 * `rank`/`topIds`/`decimals`/`figure`/`itemKind` (today's board rank, crown,
 * figure and board-or-bars rule). Stored shapes: `tests/fixtures/plots/podium-*.stored.v1.json`.
 */

import { podiumDenseRanks, podiumDisplayDecimals } from '../../slides/podium-presentation'

import { RankData, RankItem, RankLabels } from '@digital-twins/slide-schemas/generated/plot-types'
import { PlotReadAdapter, StoredPlotPayload } from '../contract'

/** The deck `podium` slide as stored (`podium.schema.json`), plot optional. */
export interface StoredPodiumSlide {
  type: 'podium'
  entries: Array<{
    id: string
    rank: number
    label: string
    score?: number | null
    badgeLabel?: string | null
    imageUrl?: string | null
  }>
  scoreLabel?: string | null
  scoreUnit?: 'percent' | 'index' | null
  tie?: boolean | null
  tieLabel?: string | null
  /** The rank badge templates in the deck language (AI part 3); absent on decks stored before them. */
  rankLabels?: Partial<Pick<RankLabels, 'plot_rank_n' | 'plot_rank_winner' | 'winner'>> | null
  plot?: { kind: string; data: unknown } | null
}

/** The summary card `podium` payload as stored (`RankingPayload`). */
export interface StoredPodiumCard {
  shape: 'podium'
  basis?: string | null
  items: Array<{
    id: string
    label: string
    tag?: string | null
    imageUrl?: string | null
    previewKind?: 'image' | 'snapshot' | 'video' | null
    respondentCopy?: string | null
    value: number
    valueLabel?: string | null
    isEscape?: boolean
    rank?: number | null
  }>
  scoreLabel?: string | null
  unit?: string | null
  baseSize?: number | null
  isTie?: boolean
  /** A crowned board's chip, a label key (`podium_board.LEAD_CHIPS`). */
  leadChip?: RankData['leadChip'] | null
  topIds?: Array<string> | null
  decimals?: number | null
  figure?: 'pick_share' | 'composite_index' | 'weighted_score' | 'share' | 'mean' | null
  itemKind?: 'tested_asset' | 'text' | null
  /** The board's bar scale (`podium_board.board_scale`); absent on cards stored before it. */
  scale?: { min: number; max: number } | null
}

/** Tested assets ride the podium board; text items (pain points, claims) draw as ranking bars. */
export type PodiumCardLayout = 'board' | 'bars'

/**
 * The strings today's slide renderer printed for a stored deck (`#2`,
 * `#1 Winner`, `Too close to call`). Legacy only: a newly built slide carries
 * its chrome in `plot.data.labels`, in the deck language.
 */
export const LEGACY_DECK_PODIUM_LABELS: Partial<RankLabels> = {
  plot_rank_n: '#{rank}',
  plot_rank_winner: '#1 {winner}',
  winner: 'Winner',
  too_close_to_call: 'Too close to call',
}

/** Today's picture podium drew ranks one to three on the board, the rest as rows beneath. */
const LEGACY_BOARD_SIZE = 3

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function scoreUnitFigure(unit: StoredPodiumSlide['scoreUnit']): { unit: string; figure: 'pick_share' | 'composite_index' } {
  if (unit === 'percent') {
    return { unit: 'percent_0_100', figure: 'pick_share' }
  }
  return { unit: 'index', figure: 'composite_index' }
}

/** Legacy only: a stored card named no figure, so today's reading of its unit and basis. */
function legacyCardFigure(card: StoredPodiumCard): 'pick_share' | 'weighted_score' | 'composite_index' {
  if (card.unit === 'percent_0_100') {
    return 'pick_share'
  }
  if (card.basis === 'scored') {
    return 'weighted_score'
  }
  return 'composite_index'
}

function cardItem(item: StoredPodiumCard['items'][number], rank: number): RankItem {
  return {
    id: item.id,
    label: item.label,
    tag: item.tag ?? null,
    value: item.value,
    rank,
    lowBase: false,
    colorSlot: null,
    imageUrl: item.imageUrl ?? null,
    previewKind: item.previewKind ?? null,
    ...(item.respondentCopy ? { respondentCopy: item.respondentCopy } : {}),
    ...(item.isEscape ? { isEscape: true } : {}),
  }
}

/** A deck podium slide stored before `plot`: today's reader, moved here. */
export const legacyDeckSlidePodiumAdapter: PlotReadAdapter<'podium', StoredPodiumSlide> = {
  kind: 'podium',
  source: { surface: 'deck-slide', shape: 'podium' },
  isLegacy: true,
  accepts(stored: unknown): stored is StoredPodiumSlide {
    return isRecord(stored) && stored.type === 'podium' && Array.isArray(stored.entries)
  },
  read(stored: StoredPodiumSlide): StoredPlotPayload<'podium'> {
    const entries = stored.entries
    const { unit, figure } = scoreUnitFigure(stored.scoreUnit)
    return {
      kind: 'podium',
      data: {
        items: entries.map((entry) => ({
          id: entry.id,
          label: entry.label,
          tag: entry.badgeLabel ?? null,
          value: entry.score ?? Number.NaN,
          rank: entry.rank,
          lowBase: false,
          colorSlot: null,
          imageUrl: entry.imageUrl ?? null,
          previewKind: null,
        })),
        unit,
        figure,
        figureLabel: stored.scoreLabel ?? null,
        // Legacy display rounding: stored slides carry no `decimals`, and the
        // slide printed the fewest digits that kept unequal scores apart.
        decimals: podiumDisplayDecimals(entries.map((entry) => entry.score)) as 0 | 1 | 2 | 3,
        topIds: entries.filter((entry) => entry.rank === 1).map((entry) => entry.id),
        tie: Boolean(stored.tie),
        basis: 'respondents',
        labels: {
          ...LEGACY_DECK_PODIUM_LABELS,
          ...(stored.rankLabels ?? {}),
          ...(stored.tieLabel?.trim() ? { too_close_to_call: stored.tieLabel } : {}),
        },
      },
    }
  },
}

/** A summary podium card carrying Python's rank, winner, decimals, figure and item kind. */
export const summaryCardPodiumAdapter: PlotReadAdapter<'podium', StoredPodiumCard> = {
  kind: 'podium',
  source: { surface: 'summary-card', shape: 'podium' },
  isLegacy: false,
  accepts(stored: unknown): stored is StoredPodiumCard {
    if (!isRecord(stored) || stored.shape !== 'podium' || !Array.isArray(stored.items)) {
      return false
    }
    const items = stored.items as Array<Record<string, unknown>>
    return Array.isArray(stored.topIds)
      && typeof stored.decimals === 'number'
      && typeof stored.figure === 'string'
      && typeof stored.itemKind === 'string'
      && items.every((item) => typeof item.rank === 'number')
  },
  read(stored: StoredPodiumCard): StoredPlotPayload<'podium'> {
    return {
      kind: 'podium',
      data: {
        items: stored.items.map((item) => cardItem(item, item.rank as number)),
        unit: stored.unit ?? 'index',
        figure: stored.figure as NonNullable<StoredPodiumCard['figure']>,
        figureLabel: stored.scoreLabel ?? null,
        decimals: stored.decimals as 0 | 1 | 2 | 3,
        topIds: [...(stored.topIds ?? [])],
        tie: Boolean(stored.isTie),
        ...(stored.leadChip ? { leadChip: stored.leadChip } : {}),
        ...(stored.scale ? { scale: { min: stored.scale.min, max: stored.scale.max } } : {}),
        basis: 'respondents',
        base: stored.baseSize ?? null,
        labels: {},
      },
    }
  },
}

/**
 * A summary podium card stored before the stamps: today's position numbering
 * (#1, #2, ... in stored order, the tail continuing after the board, the
 * escape row outside the race), today's board steps (dense on the stored
 * values for the three tiles) and today's crown (the first entrant, the top
 * two on `isTie`). Dense ranks are for stamped payloads only. The printed
 * figure stays the caller's legacy formatter, and pictures are drawn only when
 * every entrant carries one (today's `isMediaRanking`).
 */
export const legacySummaryCardPodiumAdapter: PlotReadAdapter<'podium', StoredPodiumCard> = {
  kind: 'podium',
  source: { surface: 'summary-card', shape: 'podium' },
  isLegacy: true,
  accepts(stored: unknown): stored is StoredPodiumCard {
    return isRecord(stored) && stored.shape === 'podium' && Array.isArray(stored.items)
  },
  read(stored: StoredPodiumCard): StoredPlotPayload<'podium'> {
    const entrants = stored.items.filter((item) => !item.isEscape)
    // Today's picture rule for these cards: every entrant has one, or none is drawn.
    const hasPictures = entrants.length >= 2 && entrants.every((item) => Boolean(item.imageUrl?.trim()))
    // Today's numbering: the stored order is the rank order, one position each,
    // so the tail after a board of three counts on from #4. The board's own
    // ranks were dense on the stored values, so equal scores shared a rank.
    const boardRanks = hasPictures ? podiumDenseRanks(entrants.slice(0, LEGACY_BOARD_SIZE).map((item) => item.value)) : []
    const rankOf = (index: number) => (index < boardRanks.length ? boardRanks[index] : index + 1)
    let escapeRank = entrants.length
    const items = stored.items.map((item) => {
      const read = cardItem(item, item.isEscape ? ++escapeRank : rankOf(entrants.indexOf(item)))
      return hasPictures ? read : { ...read, imageUrl: null, previewKind: null }
    })
    const topCount = stored.isTie ? Math.min(2, entrants.length) : Math.min(1, entrants.length)
    return {
      kind: 'podium',
      data: {
        items,
        unit: stored.unit ?? 'index',
        figure: legacyCardFigure(stored),
        figureLabel: stored.scoreLabel ?? null,
        decimals: 1,
        topIds: entrants.slice(0, topCount).map((item) => item.id),
        tie: Boolean(stored.isTie),
        basis: 'respondents',
        base: stored.baseSize ?? null,
        labels: {},
      },
    }
  },
}

/** Every podium read adapter, stamped shapes first. */
export const PODIUM_READ_ADAPTERS = [
  summaryCardPodiumAdapter,
  legacySummaryCardPodiumAdapter,
  legacyDeckSlidePodiumAdapter,
] as const

/** The stored deck slide's plot when Python stamped one, else its legacy read. */
export function readPodiumSlide(slide: StoredPodiumSlide): { payload: StoredPlotPayload<'podium'>; isStamped: boolean } {
  const plot = slide.plot
  if (isRecord(plot) && plot.kind === 'podium' && isRecord(plot.data) && Array.isArray(plot.data.items)) {
    return { payload: plot as unknown as StoredPlotPayload<'podium'>, isStamped: true }
  }
  return { payload: legacyDeckSlidePodiumAdapter.read(slide), isStamped: false }
}

/**
 * Today's board-or-bars rule for a stored card that names no item kind: the
 * board only when every entrant (two or more) carries a picture, else bars.
 */
function legacyCardLayout(card: StoredPodiumCard): PodiumCardLayout {
  const entrants = card.items.filter((item) => !item.isEscape)
  const hasPictures = entrants.length >= 2 && entrants.every((item) => Boolean(item.imageUrl?.trim()))
  return hasPictures ? 'board' : 'bars'
}

/** A stored summary podium card through the first adapter that accepts it, with its layout. */
export function readPodiumCard(card: StoredPodiumCard): {
  payload: StoredPlotPayload<'podium'>
  isLegacy: boolean
  layout: PodiumCardLayout
} {
  if (summaryCardPodiumAdapter.accepts(card)) {
    return {
      payload: summaryCardPodiumAdapter.read(card),
      isLegacy: false,
      layout: card.itemKind === 'tested_asset' ? 'board' : 'bars',
    }
  }
  return { payload: legacySummaryCardPodiumAdapter.read(card), isLegacy: true, layout: legacyCardLayout(card) }
}
